/**
 * Job colours.
 *
 * A station's colour says which row of the board you are looking at, and never
 * changes. A job's colour is the one that travels: it is how you follow one kitchen
 * across nine stations and three weeks without reading every chip. So the palette is
 * highlighter-like — light enough to read dark text through, saturated enough to pick
 * out against the muted station backgrounds it sits on.
 */
export const JOB_COLORS = [
  '#fde68a', // amber
  '#bbf7d0', // green
  '#bfdbfe', // blue
  '#fbcfe8', // pink
  '#ddd6fe', // violet
  '#fed7aa', // orange
  '#a5f3fc', // cyan
  '#fef08a', // yellow
  '#c7d2fe', // indigo
  '#f5d0fe', // fuchsia
  '#d9f99d', // lime
  '#e9d5ff', // purple
] as const;

/**
 * A stable colour for a job that has none stored — everything created before jobs had
 * colours. Derived from the id, so the same job keeps the same colour on every screen
 * and every reload without a migration writing to every document.
 */
export function jobColorFor(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return JOB_COLORS[hash % JOB_COLORS.length]!;
}

/**
 * Black or white, whichever stays legible on the given background. The palette above is
 * all light, but a job's colour is editable and somebody will eventually choose navy.
 */
export function readableInk(hex: string): '#000000' | '#ffffff' {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return '#000000';
  const n = parseInt(m[1]!, 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  // Rec. 601 luma, which is plenty for a two-way choice.
  return (0.299 * r! + 0.587 * g! + 0.114 * b!) / 255 > 0.6 ? '#000000' : '#ffffff';
}
